import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

type Props = {
  article: Article;
  variant?: "default" | "rank";
  rank?: number;
};

export default function ArticleCard({ article, variant = "default", rank }: Props) {
  if (variant === "rank") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-4 border-b border-background-200 py-4 transition-colors last:border-b-0 hover:bg-background-100 cursor-pointer"
      >
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 font-heading text-sm font-black text-foreground-500 group-hover:bg-primary-500 group-hover:text-background-50">
          {String(rank ?? 0).padStart(2, "0")}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-medium leading-6 text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
            {article.title}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-price-tag-3-line text-primary-500"></i>
              {article.categoryName}
            </span>
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-600"></i>
              {article.hot}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.id}`} className="relative block cursor-pointer">
        <div className="h-44 w-full overflow-hidden md:h-48">
          <img
            src={article.cover}
            alt={`${article.title} - ${article.categoryName}配图`}
            title={`${article.title} - ${article.categoryName}`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2 py-1 text-[11px] font-medium text-background-50">
          {article.categoryName}
        </span>
        {article.badge && (
          <span className="absolute right-3 top-3 rounded-md bg-foreground-950/80 px-2 py-1 text-[11px] font-medium text-background-50">
            {article.badge}
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 font-heading text-base font-bold leading-7 text-foreground-950 md:text-lg">
          <Link to={`/article/${article.id}`} className="transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-3 line-clamp-2 flex-1 text-sm leading-7 text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-background-200 pt-4 text-xs text-foreground-500">
          <span>{article.author}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.views}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.readTime} 分钟
          </span>
        </div>
      </div>
    </article>
  );
}